// ─────────────────────────────────────────────────────────────────────────
// 한 코드 = 한 기기 — 마지막에 코드를 넣은 기기가 그 코드를 갖는다(v2, 담당자 2026-10-27).
//
// 종전(v1)은 기기 제한이 없었다: 같은 코드를 두 기기에 넣으면 둘 다 계속 썼고, 좌석 문서
// 하나(lastSeenAt·activeMs·location)에 두 기기 값이 번갈아 덮여 콘솔 참여율이 흐려졌다.
// 이제 코드를 **직접 넣은** 기기가 주인이 되고, 이전 기기는 닫혀 코드칸으로 돌아간다.
//
//   기관 좌석(CLS-) — Firestore seats/{code}/holder/current = { uid(익명 로그인), claimedAt, via }
//                     입장해 있는 동안 그 문서를 지켜본다(onSnapshot) → 즉시 닫힌다.
//   개인 코드(GAS)  — verify-license(claimSession:true) 가 sessionToken 을 새로 발급하고
//                     validate-session 이 «지금 토큰이 내 것인가»를 답한다. GAS 는 밀어주지
//                     못하므로 앱이 다시 물을 때(로드·탭 복귀·주기) 닫힌다.
//
// ★주인은 **사람이 코드를 넣을 때만** 바뀐다(코드칸·좌석 링크·QR). 새로고침·재방문의 자동
//  입장은 확인만 한다 — 자동 입장이 주인을 가져오면 사람 손 없는 새로고침(폰 브라우저가 뒤 탭을 다시 읽기,
//  배포 뒤 자동 새로고침 lib/chunkRecovery)이 좌석을 가져가 지금 실습 중인 기기를 닫는다.
//  담당자 2026-10-28: «입력하면 들어가고, 다른 기기에서 또 입력하면 기존 기기는 끊기고, 첫 기기에서 다시 입력하면
//  다시 뺏어 온다» — 뺏는 건 언제나 입력이다.
//
// 이 파일은 판정만 한다(순수). 읽고 쓰는 일은 utils/licenseService.js 가 한다.
// ─────────────────────────────────────────────────────────────────────────

/** 좌석 주인 문서 id — seats/{code}/holder/{HOLDER_DOC_ID} */
export const HOLDER_DOC_ID = 'current';

/** 주인을 가져온 길 — 규칙(firestore.rules)이 이 셋만 받는다. */
export const HOLDER_VIA = ['code', 'link', 'qr'];

/**
 * 밀려난 기기에 보이는 말. 사실만 적는다 — 경고·재촉 금지(CLAUDE.md 규칙 5).
 * 코드칸을 미리 채우지 않는다: 밀려난 쪽이 실습실 공용 PC 일 수 있고, 채워 두면 다음 사람이
 * 버튼 한 번으로 그 자리를 가져간다.
 */
export const TAKEN_NOTICE = '이 코드로 다른 기기에서 입장해 이 화면은 닫혔어요. 여기서 계속하려면 코드를 다시 입력하세요.';

/**
 * 개인 코드를 다시 묻는 주기 — 탭이 보이는 동안만. GAS 는 변화를 밀어주지 못해 물어야만 안다.
 * 고른 값이다(데이터에서 뽑은 임계가 아니다): 사용자 한 명이 시간당 12번 묻는다.
 * 탭 복귀(visibilitychange) 때도 묻기 때문에, 다른 기기에서 넣고 돌아오면 그때 바로 닫힌다.
 */
export const PERSONAL_RECHECK_MS = 5 * 60 * 1000;

/**
 * 좌석 주인 문서와 이 기기의 uid 를 견준다.
 * @returns {'none'|'mine'|'taken'|'unknown'}
 *   - none    : 주인 기록 없음(v2 전에 들어온 좌석) → 아무도 끊지 않는다
 *   - mine    : 이 기기가 주인
 *   - taken   : 다른 기기가 주인 → 이 기기를 닫는다
 *   - unknown : 이 기기의 uid 를 모른다 → 판정하지 않는다(못 본 것을 '남의 것'으로 치지 않는다, 규칙 3)
 */
export function holderVerdict(holder, myUid) {
  const owner = holder && typeof holder.uid === 'string' && holder.uid ? holder.uid : null;
  if (!owner) return 'none';
  if (!myUid) return 'unknown';
  return owner === myUid ? 'mine' : 'taken';
}

/**
 * GAS validate-session 응답을 읽는다.
 * @returns {'valid'|'taken'|'unknown'}
 *   - taken 은 **명시적인 another-device 일 때만**. 그 밖의 모든 것(만료·코드 없음·이 액션을 모르는
 *     옛 GAS·깨진 응답)은 unknown — 이 경로는 기기만 판정한다. 만료는 종전대로 로컬 만료일이 맡는다.
 */
export function personalSessionVerdict(res) {
  if (!res || typeof res !== 'object') return 'unknown';
  if (res.valid === true) return 'valid';
  if (res.valid === false && res.reason === 'another-device') return 'taken';
  return 'unknown';
}
